import { unified } from 'unified'
import remarkParse from 'remark-parse'
import remarkGfm from 'remark-gfm'
import remarkBreaks from 'remark-breaks'
import remarkRehype from 'remark-rehype'
import rehypeSanitize from 'rehype-sanitize'
import rehypeStringify from 'rehype-stringify'
import { styles } from '@mattilsynet/design'

const markdownHeadingSizes = {
  1: 'md',
  2: 'sm',
  3: 'xs'
} as const

type MarkdownHeadingLevel = keyof typeof markdownHeadingSizes

export const getMarkdownHeadingAttrs = (
  level: number
): Record<string, string> => {
  const size = markdownHeadingSizes[level as MarkdownHeadingLevel]

  if (!size) return {}

  return {
    class: styles.heading,
    'data-size': size
  }
}

const getMarkdownHeadingHastProperties = (level: number) => {
  const attrs = getMarkdownHeadingAttrs(level)

  if (!attrs.class) return attrs

  const { class: className, ...properties } = attrs

  return {
    ...properties,
    className: className.split(' ')
  }
}

type HtmlNode = {
  type?: string
  tagName?: string
  properties?: Record<string, unknown>
  children?: HtmlNode[]
}

const addMarkdownHeadingAttrs = () => {
  const addHeadingAttrs = (node: HtmlNode) => {
    if (!('children' in node)) return

    if (node.type === 'element') {
      const level = Number(node.tagName?.match(/^h([1-6])$/)?.[1])
      const attrs = getMarkdownHeadingHastProperties(level)

      if (Object.keys(attrs).length > 0) {
        node.properties = {
          ...node.properties,
          ...attrs
        }
      }
    }

    node.children?.forEach(addHeadingAttrs)
  }

  return (tree: unknown) => {
    addHeadingAttrs(tree as HtmlNode)
  }
}

/**
 * Shared remark → HTML pipeline used by all read-only markdown renderers.
 *
 * Uses the same remark parser as the Milkdown editor, ensuring the editor and
 * the read-only view always agree on how markdown is parsed.
 *
 * Features:
 * - CommonMark (headings, bold, italic, lists, blockquote, links, code)
 * - GFM (tables, strikethrough, task list items)
 * - Soft line breaks → <br> (matches Milkdown's remarkLineBreak behaviour)
 * - rehype-sanitize removes any dangerous HTML before rendering
 */
export const markdownProcessor = unified()
  .use(remarkParse)
  .use(remarkGfm)
  .use(remarkBreaks)
  .use(remarkRehype)
  .use(rehypeSanitize)
  .use(addMarkdownHeadingAttrs)
  .use(rehypeStringify)

/**
 * Synchronously convert a markdown string to a sanitized HTML string.
 */
export function renderMarkdown(content: string): string {
  return String(markdownProcessor.processSync(content))
}
